import {IOrganizationAdvertiserLogin} from "../../interfaces/IOrganizationAdvertiserLogin";
import {Grid, Mui, MuiIcons} from "@leap4/frontend";
import {IOrganizationPartners} from "./PublisherLogins.container";
import React, {useCallback, useContext, useMemo} from "react";
import {OrganizationContext} from "../OrganizationContex";
import {buildStatsUrl} from "../../../../utils/buildStatsUrl";
import {LoginUrlsTable} from "./LoginUrlsTable";

const {Autocomplete, Button, Chip, MenuItem, Select, TextField} = Mui;
const {Delete: DeleteIcon} = MuiIcons;

// Type for Select onChange event
type SelectChangeEvent = Parameters<NonNullable<React.ComponentProps<typeof Select>['onChange']>>[0];

interface IPublisherLoginsAccessProps {
    organization: IOrganizationPartners,
    isLoading: boolean,
    availableOrganizations: Array<IOrganizationAdvertiserLogin>,
    onUpdateSelectedOrgs: (selectedOrg: string, currentOrgId: string | undefined) => void,
    onPartnersChange: (choosenPartners: string[], currentOrgId: string) => void,
    onOrganizationRemove: (selectedOrg: string | undefined) => void,
}

interface IPartnerOption {
    id: string;
    name: string;
}

export const PublisherLoginsAccess = ({
                                          organization,
                                          isLoading,
                                          availableOrganizations,
                                          onUpdateSelectedOrgs,
                                          onPartnersChange,
                                          onOrganizationRemove,
}: IPublisherLoginsAccessProps) => {

    const {org, selectedPartners, optionalPartners} = organization
    const {clientCredentials, savedPartnersByOrg} = useContext(OrganizationContext);

    const handleOrgChange = (event: SelectChangeEvent) => {
        const currentOrgId = org?._id
        onUpdateSelectedOrgs(event.target.value as string, currentOrgId)
    }

    const handlePartnersChange = (chosenPartners: string[]) => {
        onPartnersChange(chosenPartners, org._id)
    }

    const handleOrganizationRemove = () => {
        onOrganizationRemove(org._id)
    }

    const validPartners: IPartnerOption[] = useMemo(() => {
        const arr = optionalPartners as IPartnerOption[];
        return arr[0]?.id ? arr : [];
    }, [optionalPartners]);

    const selectedAsOptions: IPartnerOption[] = useMemo(
        () => selectedPartners.map(id => {
            const found = validPartners.find(p => p.id === id);
            return found ?? {id, name: id};
        }),
        [selectedPartners, validPartners]
    );

    const unsavedPartnerIds: string[] = useMemo(() => {
        const saved = (org?._id && savedPartnersByOrg[org._id]) || [];
        return selectedPartners.filter(id => !saved.includes(id));
    }, [selectedPartners, savedPartnersByOrg, org?._id]);

    const canCopy = !!clientCredentials?.clientKey;

    const buildUrl = useCallback(
        (demandPartnerId: string) =>
            buildStatsUrl({
                demandPartnerId,
                clientKey: clientCredentials?.clientKey ?? "",
                clientSecret: clientCredentials?.visibleSecret ?? "{ClientSecret}",
            }),
        [clientCredentials?.clientKey, clientCredentials?.visibleSecret]
    );

    if (optionalPartners[0]) {
        if (typeof optionalPartners[0] === "string") {
            return <></>;
        }
    }
    return (
        <Grid container  justifyContent={"space-between"} spacing={0.5} mb={2}>
            <Grid item xs={2}>

                <Select size={"small"} disabled={isLoading}
                        onChange={handleOrgChange}
                        value={org?._id || 'NULL'}
                        fullWidth
                >

                    <MenuItem disabled value={'NULL'}>
                        <em>Select Organization</em>
                    </MenuItem>

                    {availableOrganizations.map(o => {
                        return <MenuItem key={o._id} divider value={o?._id || 'ID'}>{o?.name}</MenuItem>
                    })}

                </Select>
            </Grid>
            <Grid item xs={4.5}>
                <Autocomplete
                    multiple
                    size="small"
                    options={validPartners}
                    value={selectedAsOptions}
                    getOptionLabel={(o: IPartnerOption) => o?.name ?? ""}
                    isOptionEqualToValue={(o: IPartnerOption, v: IPartnerOption) => o?.id === v?.id}
                    filterSelectedOptions
                    onChange={(_e: React.SyntheticEvent, value: IPartnerOption[]) =>
                        handlePartnersChange(value.map(v => v.id))
                    }
                    renderTags={(values: IPartnerOption[], getTagProps: (p: {index: number}) => {key: React.Key} & Record<string, unknown>) =>
                        values.map((opt, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    label={opt.name}
                                    size="small"
                                    color="primary"
                                />
                            );
                        })
                    }
                    renderInput={(params: React.ComponentProps<typeof TextField>) => (
                        <TextField {...params} label="Demand Partners" />
                    )}
                />
            </Grid>

            <Grid item xs={1.5}>
                <Button fullWidth variant="outlined" disabled={!org?._id} startIcon={<DeleteIcon/>}
                        onClick={handleOrganizationRemove}>
                    Delete
                </Button>

            </Grid>

            {selectedAsOptions.length > 0 && (
                <Grid item xs={12} mt={1}>
                    <LoginUrlsTable
                        partners={selectedAsOptions}
                        buildUrl={buildUrl}
                        disabled={!canCopy}
                        title="Demand Partners"
                        unsavedPartnerIds={unsavedPartnerIds}
                    />
                </Grid>
            )}

        </Grid>
    )
}
